import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { exportPdiReportPdf } from "@/lib/pdiReportPdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Save, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export const PDI_STATUS = {
  not_started: { label: "PDI Not Started", cls: "bg-slate-100 text-slate-600" },
  in_progress: { label: "PDI In Progress", cls: "bg-amber-50 text-amber-700" },
  approved: { label: "PDI Approved", cls: "bg-emerald-50 text-emerald-700" },
};

// Final Inspection Report (PDI) for one customer DC — see backend/pdi_report.py.
// Header + one section per DC line come pre-filled from the DC; the inspector
// records Observation + OK/NG per parameter, signs off, and picks who
// approves. Only that approver (or admin) can approve; approval locks it.
export default function PdiReportDialog({ dc, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [approvalDate, setApprovalDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !dc) return;
    setReport(null);
    Promise.all([api.get(`/pdi-reports/dc/${dc.id}`), api.get("/pdi-reports/approvers")])
      .then(([r, a]) => {
        setReport(r.data);
        setApprovers(a.data);
        setApprovalDate(r.data.approval_date || new Date().toISOString().slice(0, 10));
      })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load PDI Report");
        onOpenChange(false);
      });
  }, [open, dc]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = report?.status === "approved";
  const set = (patch) => setReport((r) => ({ ...r, ...patch }));
  const setParam = (li, pi, patch) => setReport((r) => ({
    ...r,
    lines: r.lines.map((ln, i) => (i !== li ? ln : { ...ln, params: ln.params.map((p, j) => (j === pi ? { ...p, ...patch } : p)) })),
  }));

  const payload = () => ({
    report_no: report.report_no,
    report_date: report.report_date,
    inspector_name: report.inspector_name,
    inspector_signature: report.inspector_signature,
    approved_by_id: report.approved_by_id || "",
    lines: report.lines.map((ln) => ({ params: ln.params.map(({ observation, result }) => ({ observation, result })) })),
  });

  const save = async (quiet = false) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/pdi-reports/dc/${dc.id}`, payload());
      setReport(data);
      onChanged?.(data);
      if (!quiet) toast.success("PDI Report saved");
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!(await save(true))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pdi-reports/dc/${dc.id}/approve`, { approval_date: approvalDate });
      setReport(data);
      onChanged?.(data);
      toast.success("PDI Report approved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pdi-reports/dc/${dc.id}/reopen`);
      setReport(data);
      onChanged?.(data);
      toast.success("PDI Report reopened");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reopen failed");
    } finally {
      setBusy(false);
    }
  };

  const canApprove = !locked && report?.id && report?.approved_by_id &&
    (report.approved_by_id === user?.id || user?.role === "admin");
  const st = PDI_STATUS[report?.status] || PDI_STATUS.not_started;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="pdi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            PDI Report — {dc?.code}
            {report && <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${st.cls}`} data-testid="pdi-dialog-status">{st.label}</span>}
          </DialogTitle>
          <DialogDescription>Final Inspection Report. Fill in an Observation and Result for each parameter.</DialogDescription>
        </DialogHeader>

        {!report ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="border border-slate-300 rounded-md overflow-hidden text-sm">
              <div className="grid grid-cols-3 border-b border-slate-300">
                <div className="p-2 font-bold text-center border-r border-slate-300">{report.company_name}</div>
                <div className="p-2 font-extrabold text-center uppercase tracking-wide border-r border-slate-300">{report.title}</div>
                <div className="p-2 text-xs text-center text-slate-600">Doc No: {report.doc_no}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                <div><Label>Customer</Label><Input value={report.customer} disabled /></div>
                <div><Label>Report No</Label><Input value={report.report_no} disabled={locked} onChange={(e) => set({ report_no: e.target.value })} data-testid="pdi-report-no" /></div>
                <div><Label>Reference No / Order No</Label><Input value={report.reference_no} disabled /></div>
                <div><Label>Date</Label><Input type="date" value={report.report_date} disabled={locked} onChange={(e) => set({ report_date: e.target.value })} data-testid="pdi-report-date" /></div>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-xs" data-testid="pdi-table">
                <thead>
                  <tr className="bg-slate-50 text-left uppercase tracking-wider text-[10px] text-slate-500">
                    <th className="p-2">Sl</th><th className="p-2">Part Name</th><th className="p-2">Part No</th>
                    <th className="p-2 text-right">Qty</th><th className="p-2">Unit</th><th className="p-2">Parameter</th>
                    <th className="p-2">Specification</th><th className="p-2">Method</th>
                    <th className="p-2 min-w-[180px]">Observation</th><th className="p-2 w-24">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((ln, li) => ln.params.map((p, pi) => (
                    <tr key={`${li}-${pi}`} className={pi === 0 ? "border-t-2 border-slate-300" : "border-t border-slate-100"}>
                      {pi === 0 && (
                        <>
                          <td rowSpan={ln.params.length} className="p-2 align-top font-semibold">{ln.sl_no}</td>
                          <td rowSpan={ln.params.length} className="p-2 align-top">{ln.part_name}</td>
                          <td rowSpan={ln.params.length} className="p-2 align-top mono">{ln.part_no}</td>
                          <td rowSpan={ln.params.length} className="p-2 align-top text-right mono">{ln.qty}</td>
                          <td rowSpan={ln.params.length} className="p-2 align-top">{ln.unit}</td>
                        </>
                      )}
                      <td className="p-2 font-medium">{p.parameter}</td>
                      <td className="p-2 text-slate-600">{p.specification}</td>
                      <td className="p-2 text-slate-600">{p.method}</td>
                      <td className="p-1">
                        <Input className="h-8 text-xs" value={p.observation} disabled={locked}
                          onChange={(e) => setParam(li, pi, { observation: e.target.value })} data-testid={`pdi-obs-${li}-${pi}`} />
                      </td>
                      <td className="p-1">
                        <Select value={p.result || "none"} disabled={locked} onValueChange={(v) => setParam(li, pi, { result: v === "none" ? "" : v })}>
                          <SelectTrigger className={`h-8 text-xs ${p.result === "NG" ? "text-red-600 font-semibold" : p.result === "OK" ? "text-emerald-700 font-semibold" : ""}`} data-testid={`pdi-result-${li}-${pi}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            <SelectItem value="OK">OK</SelectItem>
                            <SelectItem value="NG">NG</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  )))}
                  {report.lines.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-slate-500">This DC has no lines.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-slate-200 rounded-md p-3">
              <div><Label>Inspector Name</Label><Input value={report.inspector_name} disabled={locked} onChange={(e) => set({ inspector_name: e.target.value })} data-testid="pdi-inspector-name" /></div>
              <div>
                <Label>Inspector Signature</Label>
                <Input value={report.inspector_signature} disabled={locked} placeholder="Type your name to sign off"
                  onChange={(e) => set({ inspector_signature: e.target.value })} className="italic" data-testid="pdi-inspector-signature" />
              </div>
              <div>
                <Label>Approved By</Label>
                <Select value={report.approved_by_id || "none"} disabled={locked} onValueChange={(v) => set({ approved_by_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="pdi-approved-by"><SelectValue placeholder="Select approver" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Select approver —</SelectItem>
                    {approvers.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Approval Date</Label>
                <Input type="date" value={approvalDate} disabled={locked || !canApprove} onChange={(e) => setApprovalDate(e.target.value)} data-testid="pdi-approval-date" />
                {!locked && report.approved_by_id && !canApprove && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    {report.id ? `Waiting for ${report.approved_by_name || "the approver"} to approve.` : "Save the report to send it for approval."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {report && (
            <Button variant="outline" onClick={() => exportPdiReportPdf(report)} data-testid="pdi-dialog-download">
              <Download className="w-4 h-4 mr-1.5" /> Download PDI Report
            </Button>
          )}
          {locked && user?.role === "admin" && (
            <Button variant="outline" onClick={reopen} disabled={busy} data-testid="pdi-reopen-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
            </Button>
          )}
          {report && !locked && (
            <Button variant="outline" onClick={() => save()} disabled={busy} data-testid="pdi-save-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          )}
          {canApprove && (
            <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pdi-approve-btn">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Approve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
